import { encodeSctList } from '../core/rfc6962/sct';
import type { Sct } from '../core/rfc6962/types';
import type { LogDescriptor } from '../core/log/client';

export interface SctRecord {
  log: LogDescriptor;
  sct: Sct;
  verified: boolean | 'unverifiable';
}

/** JSON bundle: each SCT as the RFC 6962 add-chain response plus its TLS encoding and the log's name. */
export function sctBundleJson(records: SctRecord[], subject: string): string {
  return JSON.stringify(
    {
      subject,
      created: new Date().toISOString(),
      scts: records.map((r) => ({
        log: { description: r.log.description, operator: r.log.operator, url: r.log.kind === 'tiled' ? r.log.submissionUrl : r.log.url, log_id: r.sct.logId.toBase64() },
        signature_verified: r.verified,
        sct_version: r.sct.version,
        id: r.sct.logId.toBase64(),
        timestamp: r.sct.timestamp,
        extensions: r.sct.extensions.toBase64(),
        signature: (() => {
          const ds = new Uint8Array(4 + r.sct.signature.signature.length);
          ds[0] = r.sct.signature.hashAlgorithm;
          ds[1] = r.sct.signature.signatureAlgorithm;
          ds[2] = r.sct.signature.signature.length >> 8;
          ds[3] = r.sct.signature.signature.length & 0xff;
          ds.set(r.sct.signature.signature, 4);
          return ds.toBase64();
        })(),
        sct_tls_base64: r.sct.raw.toBase64(),
      })),
      sct_list_tls_base64: encodeSctList(records.map((r) => r.sct.raw)).toBase64(),
    },
    null,
    2,
  );
}

const safe = (s: string): string => s.replace(/[^A-Za-z0-9._-]+/g, '_').slice(0, 60);

/** add-chain JSON for one SCT (what a log answers with). */
export function sctAddChainJson(sct: Sct): string {
  const ds = new Uint8Array(4 + sct.signature.signature.length);
  ds[0] = sct.signature.hashAlgorithm;
  ds[1] = sct.signature.signatureAlgorithm;
  ds[2] = sct.signature.signature.length >> 8;
  ds[3] = sct.signature.signature.length & 0xff;
  ds.set(sct.signature.signature, 4);
  return JSON.stringify({ sct_version: sct.version, id: sct.logId.toBase64(), timestamp: sct.timestamp, extensions: sct.extensions.toBase64(), signature: ds.toBase64() }, null, 2);
}

export const sctBundleFilename = (subject: string): string => `${safe(subject)}-scts.json`;
/** The TLS-encoded SignedCertificateTimestampList, as embedded in the certificate/TLS extension. */
export const sctListBytes = (records: SctRecord[]): Uint8Array => encodeSctList(records.map((r) => r.sct.raw));
export const sctListFilename = (subject: string): string => `${safe(subject)}.sctlist`;
/** One raw TLS SCT (the format web servers like nginx/Apache consume as `.sct` files). */
export const singleSctFilename = (r: SctRecord, subject: string): string => `${safe(subject)}-${safe(r.log.description)}.sct`;
